import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Config from "../utils/Config";

export const AuthContext = createContext(null);

const IDLE_TIMEOUT_MS = Config.APP.IDLE_TIMEOUT_MINUTES * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const idleTimerRef = useRef(null);
  const listenersSetRef = useRef(false);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      // console.debug("🧹 Idle timer cleared:", idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (!isAuthenticated) return;
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      // console.warn("⏰ Session timed out due to inactivity.");
      logout("timeout");
    }, IDLE_TIMEOUT_MS);
    // console.debug("✅ Idle timer started:", idleTimerRef.current);
  }, [clearIdleTimer, isAuthenticated]);

  const removeIdleTimeoutListeners = useCallback(() => {
    ["mousemove", "keydown", "scroll", "touchstart"].forEach((event) =>
      window.removeEventListener(event, resetIdleTimer)
    );
    listenersSetRef.current = false;
    // console.debug("🧹 Idle timeout listeners removed.");
  }, [resetIdleTimer]);

  const setupIdleTimeoutListeners = useCallback(() => {
    if (listenersSetRef.current || !isAuthenticated) return;
    ["mousemove", "keydown", "scroll", "touchstart"].forEach((event) =>
      window.addEventListener(event, resetIdleTimer)
    );
    listenersSetRef.current = true;
    // console.debug("✅ Idle timeout listeners set.");
  }, [resetIdleTimer, isAuthenticated]);

  const logout = useCallback(
    async (reason = null) => {
      const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
      const sessionId = localStorage.getItem("sessionId");
      const why = reason === "timeout" ? "timeout" : "manual";

      try {
        const url = `${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_LOGOUT}`;

        if (token) {
          if (why === "timeout" && navigator.sendBeacon) {
            // fire-and-forget; no console error when tab is sleeping
            const blob = new Blob(
              [JSON.stringify({ reason: why, sessionId, token })],
              { type: "application/json" }
            );
            navigator.sendBeacon(url, blob);
          } else {
            // manual logout (or no beacon) – await and swallow errors
            const controller = new AbortController();
            const t = setTimeout(() => controller.abort(), 1500);
            try {
              await fetch(url, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ reason: why, sessionId }),
                keepalive: true,
                signal: controller.signal,
              });
            } catch {
              // ignore network/abort errors
            } finally {
              clearTimeout(t);
            }
          }
        }

        // flag
        if (reason === "timeout") localStorage.setItem("loggedOutDueToIdle", "true");
        else localStorage.removeItem("loggedOutDueToIdle");

        // clear storage
        [
          "authToken", "sessionId", "activeTab", "activeTabAdmin", "fflClaimedOnce", "claimFFLData",
          "dealerId", "fflEditData", "userEmail", "claimFFLPanelStates", "postLoginRedirect",
        ].forEach(k => localStorage.removeItem(k));
        sessionStorage.removeItem("authToken");

        // reset state
        setIsAuthenticated(false);
        setUser(null);
        clearIdleTimer();
        removeIdleTimeoutListeners();
      } finally {
        setLoading(false);
      }
    },
    [clearIdleTimer, removeIdleTimeoutListeners, setIsAuthenticated, setUser, setLoading]
  );

  const updateProfile = (newData) => {
    setUser(prev => ({ ...prev, ...newData }));
  };

  const fetchUserData = useCallback(
    async (token, retryCount = 0) => {
      try {
        const res = await axios.get(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_INFO}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsAuthenticated(true);
        setUser(res.data);
        setLoading(false);
      } catch (err) {
        if ([401,403].includes(err.response?.status)) {
          logout();
        } else if (retryCount < 3) {
          clearIdleTimer();
          setTimeout(() => fetchUserData(token, retryCount + 1), 1000 * (retryCount + 1));
        } else {
          console.error("🚫 Failed to fetch user data.");
          setLoading(false);
        }
      }
    },
    [logout, clearIdleTimer]
  );

  useEffect(() => {
    const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (token?.length > 30) {
      fetchUserData(token);
    } else {
      setLoading(false);
    }

    return () => {
      clearIdleTimer();
      removeIdleTimeoutListeners();
    };
  }, [fetchUserData, clearIdleTimer, removeIdleTimeoutListeners]);

  useEffect(() => {
    if (isAuthenticated && user) {
      setupIdleTimeoutListeners();
      resetIdleTimer(); // only once, when authenticated and listeners are attached
    }
  }, [isAuthenticated, user, setupIdleTimeoutListeners, resetIdleTimer]);

  const login = (token) => {
    localStorage.setItem("authToken", token);
    localStorage.removeItem("activeTab");
    localStorage.removeItem("fflClaimedOnce");
    fetchUserData(token);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, loading, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
